import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Lenis from 'lenis';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { COMPONENT_TO_ROUTE, ROUTE_TO_COMPONENT } from './config/nav';

import Sidebar              from './components/Sidebar';
import MobileTopBar         from './components/MobileTopBar';
import MobileNavDrawer      from './components/MobileNavDrawer';
import InstagramConnectModal from './components/InstagramConnectModal';
import LoadingScreen        from './components/LoadingScreen';
import NotificationDrawer   from './components/NotificationDrawer';
import SupportChat          from './components/SupportChat';
import Tutorial             from './components/Tutorial';
import SubscriptionUpgradeModal from './components/SubscriptionUpgradeModal';
import Toast                from './components/Toast';
import Footer               from './components/Footer';
import AuthScreen           from './components/pages/AuthScreen';
import LandingPage          from './components/pages/LandingPage';
import Dashboard            from './components/pages/Dashboard';
import ContractAnalyzer     from './components/pages/ContractAnalyzer';
import NILEarningsTracker   from './components/pages/NILEarningsTracker';

const PostScheduler      = React.lazy(() => import('./components/pages/PostScheduler'));
const SocialMediaScanner = React.lazy(() => import('./components/pages/SocialMediaScanner'));
const GhostAgent         = React.lazy(() => import('./components/pages/GhostAgent'));
const ProfileSettings    = React.lazy(() => import('./components/pages/ProfileSettings'));
const NILScorePage       = React.lazy(() => import('./components/pages/NILScorePage'));
const AdminPortal        = React.lazy(() => import('./components/pages/AdminPortalV2'));
const LegalPrivacy       = React.lazy(() => import('./components/pages/LegalPrivacy'));
const LegalDataDeletion  = React.lazy(() => import('./components/pages/LegalDataDeletion'));
const LegalTerms         = React.lazy(() => import('./components/pages/LegalTerms'));
const LegalPatents       = React.lazy(() => import('./components/pages/LegalPatents'));
const FAQ                = React.lazy(() => import('./components/pages/FAQ'));

// ─── Shared UI primitives ──────────────────────────────────────────────────────

function PageTransition({ pathname, children }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.animation = 'none';
    void el.offsetHeight;
    el.style.animation = '';
  }, [pathname]);
  return <div ref={ref} className="page-enter">{children}</div>;
}

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { try { console.error('ErrorBoundary:', error, info); } catch {} }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: '100vh', background: 'var(--surface-base)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem' }}>
          <div style={{ maxWidth: 400, width: '100%', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <h1 style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Something went wrong</h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>
              {String(this.state.error?.message || 'Unknown error')}
            </p>
            <button className="btn-secondary" onClick={() => window.location.reload()}>Reload</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const SuspenseFallback = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 400 }}>
    <div style={{ textAlign: 'center' }}>
      <div className="spinner" style={{ margin: '0 auto 12px' }} />
      <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>Loading…</div>
    </div>
  </div>
);

const Lazy = ({ children }) => <React.Suspense fallback={<SuspenseFallback />}>{children}</React.Suspense>;

// ─── Main authenticated shell ─────────────────────────────────────────────────

const AppContent = () => {
  const { user, profile, loading: authLoading, isAuthenticating, signOut, apiCall, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Auth & splash state
  const hasTokenOnMount = useRef(!!localStorage.getItem('token'));
  const [showWelcomeScreen, setShowWelcomeScreen] = useState(!hasTokenOnMount.current);
  const [welcomeVisible,    setWelcomeVisible]    = useState(!hasTokenOnMount.current);
  const [showSigningInScreen, setShowSigningInScreen] = useState(hasTokenOnMount.current);
  const [showAuthScreen,   setShowAuthScreen]   = useState(false);
  const [authDefaultTab,   setAuthDefaultTab]   = useState('signin');
  const [pendingPlan,      setPendingPlan]      = useState(null);
  const [showInitialLoading, setShowInitialLoading] = useState(false);

  // App state
  const [activeComponent, setActiveComponentState] = useState('dashboard');
  const [userStats,       setUserStats]       = useState({});
  const [mobileNavOpen,   setMobileNavOpen]   = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showIgModal,     setShowIgModal]     = useState(false);
  const [pendingIgPrompt, setPendingIgPrompt] = useState(false);
  const [success, setSuccess] = useState('');
  const [error,   setError]   = useState('');
  const [ghostDraftPending, setGhostDraftPending] = useState(false);

  // Tutorial state
  const [showTutorial,    setShowTutorial]    = useState(false);
  const [tutorialLoading, setTutorialLoading] = useState(true);
  const tutorialInitRef = useRef(false);

  // Notifications
  const NOTIF_PAGE = 20;
  const [notifications,   setNotifications]   = useState([]);
  const [notifDrawerOpen, setNotifDrawerOpen] = useState(false);
  const [notifHasMore,    setNotifHasMore]    = useState(false);
  const [notifOffset,     setNotifOffset]     = useState(0);
  const unreadCount = notifications.filter(n => !n.read).length;

  // ─── Navigation ───────────────────────────────────────────────────────────

  const handleNavigation = useCallback((id) => {
    navigate(COMPONENT_TO_ROUTE[id] || '/dashboard');
  }, [navigate]);

  const setActiveComponent = useCallback((id) => handleNavigation(id), [handleNavigation]);

  useEffect(() => {
    setActiveComponentState(ROUTE_TO_COMPONENT[location.pathname] || 'dashboard');
  }, [location.pathname]);

  // ─── Lenis smooth scroll ──────────────────────────────────────────────────

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), smoothWheel: true, touchMultiplier: 1.6 });
    let rafId;
    const raf = (time) => { lenis.raf(time); rafId = requestAnimationFrame(raf); };
    rafId = requestAnimationFrame(raf);
    window.__lenis = lenis;
    return () => { cancelAnimationFrame(rafId); lenis.destroy(); delete window.__lenis; };
  }, []);

  useEffect(() => {
    const reset = () => { try { window.scrollTo({ top: 0, behavior: 'auto' }); } catch {} };
    setTimeout(reset, 20);
    setTimeout(reset, 140);
  }, [location.pathname]);

  // ─── Splash screen ────────────────────────────────────────────────────────

  useEffect(() => {
    if (hasTokenOnMount.current) { window.__nv_welcome_completed = true; return; }
    const t1 = setTimeout(() => { setWelcomeVisible(false); window.__nv_welcome_completed = true; }, 3000);
    const t2 = setTimeout(() => setShowWelcomeScreen(false), 4500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  // ─── Auth overlay ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (isAuthenticating) {
      setShowSigningInScreen(true);
    } else if (!isAuthenticating && user && profile && !authLoading) {
      const t = setTimeout(() => setShowSigningInScreen(false), 600);
      return () => clearTimeout(t);
    } else if (!isAuthenticating && !user && !authLoading) {
      setShowSigningInScreen(false);
    }
  }, [isAuthenticating, user, profile, authLoading]);

  // ─── Tutorial ─────────────────────────────────────────────────────────────

  useEffect(() => {
    const check = async () => {
      if (new URLSearchParams(window.location.search).get('skipTutorial') === '1') {
        tutorialInitRef.current = true;
        setShowTutorial(false);
        setTutorialLoading(false);
        return;
      }
      if (user && profile && !authLoading && !isAuthenticating) {
        if (!tutorialInitRef.current) {
          tutorialInitRef.current = true;
          setTutorialLoading(true);
          try {
            await new Promise(r => setTimeout(r, 800));
            setShowTutorial(profile?.tutorial_completed !== true);
          } catch { setShowTutorial(false); }
          finally { setTutorialLoading(false); }
        } else {
          setShowTutorial(profile?.tutorial_completed !== true);
          setTutorialLoading(false);
        }
      } else {
        tutorialInitRef.current = false;
        setTutorialLoading(false);
        setShowTutorial(false);
      }
    };
    check();
  }, [user, profile, authLoading, isAuthenticating]);

  const handleTutorialComplete = () => {
    setShowTutorial(false);
    if (pendingIgPrompt) {
      setTimeout(() => { setShowIgModal(true); setPendingIgPrompt(false); }, 500);
    }
  };

  const handleReplayTutorial = async () => {
    try {
      await apiCall('/api/user/reset-tutorial', { method: 'POST' });
      handleNavigation('dashboard');
      setTutorialLoading(false);
      setShowTutorial(true);
    } catch { setError('Failed to replay tutorial.'); }
  };

  useEffect(() => { window.replayTutorial  = handleReplayTutorial;        return () => { delete window.replayTutorial; };  }, []);
  useEffect(() => { window.openUpgradeModal = () => setShowUpgradeModal(true); return () => { delete window.openUpgradeModal; }; }, []);

  // ─── Ghost Agent draft dot ────────────────────────────────────────────────

  useEffect(() => {
    if (!user) return;
    apiCall('/api/ghost-agent/intake-draft/peek')
      .then(res => setGhostDraftPending(res?.pending === true))
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    const handler = () => {
      setTimeout(() => {
        apiCall('/api/ghost-agent/intake-draft/peek')
          .then(res => setGhostDraftPending(res?.pending === true))
          .catch(() => {});
      }, 10000);
    };
    window.addEventListener('contract:analyzed', handler);
    return () => window.removeEventListener('contract:analyzed', handler);
  }, [apiCall]);

  useEffect(() => {
    if (activeComponent === 'ghost') setGhostDraftPending(false);
  }, [activeComponent]);

  // ─── Notifications ────────────────────────────────────────────────────────

  useEffect(() => {
    if (!user) return;
    const fetch = () => {
      apiCall(`/api/notifications?skip=0&limit=${NOTIF_PAGE}`)
        .then(res => { setNotifications(res?.notifications || []); setNotifHasMore(res?.has_more || false); setNotifOffset(NOTIF_PAGE); })
        .catch(() => {});
    };
    fetch();
    const t = setInterval(fetch, 60_000);
    return () => clearInterval(t);
  }, [user, apiCall]);

  const loadMoreNotifs = () => {
    apiCall(`/api/notifications?skip=${notifOffset}&limit=${NOTIF_PAGE}`)
      .then(res => { setNotifications(prev => [...prev, ...(res?.notifications || [])]); setNotifHasMore(res?.has_more || false); setNotifOffset(o => o + NOTIF_PAGE); })
      .catch(() => {});
  };

  const markNotifRead = (id) => {
    apiCall(`/api/notifications/${id}/read`, { method: 'POST' }).catch(() => {});
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  };

  const markAllNotifsRead = () => {
    apiCall('/api/notifications/read-all', { method: 'POST' }).catch(() => {});
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  // ─── URL callbacks ────────────────────────────────────────────────────────

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get('newSignup') === '1' && user && profile && !authLoading) {
      if (p.get('promptInstagram') === '1') setPendingIgPrompt(true);
      // New signup with a pending plan — redirect straight to Stripe checkout
      const plan = p.get('plan');
      if (plan === 'pro' || plan === 'elite') {
        window.history.replaceState({}, document.title, window.location.pathname);
        apiCall('/api/stripe/create-subscription-checkout', {
          method: 'POST',
          body: JSON.stringify({ package_id: plan, origin_url: window.location.origin }),
        }).then(res => {
          if (res?.checkout_url) window.location.href = res.checkout_url;
        }).catch(() => {});
      } else {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, [user, profile, authLoading, apiCall]);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const igFlag = p.get('ig');
    if (igFlag === 'connected') {
      setSuccess('Instagram account connected');
      refreshProfile().catch(() => {});
      p.delete('ig'); p.delete('reason');
      window.history.replaceState({}, '', window.location.pathname + (p.toString() ? `?${p}` : ''));
    } else if (igFlag === 'error' && p.get('reason') === 'needs_page') {
      setError('Instagram requires a Facebook Page link for Creator/Business accounts.');
      setShowIgModal(true);
      p.delete('ig'); p.delete('reason');
      window.history.replaceState({}, '', window.location.pathname + (p.toString() ? `?${p}` : ''));
    }
  }, [refreshProfile]);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get('session_id') && user) window.history.replaceState({}, '', window.location.pathname);
  }, [user]);

  // ─── Stats fetch ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (!user) return;
    const run = async () => {
      try {
        if (!window.__nv_preload_shown) setShowInitialLoading(true);
        const [statsRes, earningsRes] = await Promise.allSettled([
          apiCall('/api/user/stats'),
          apiCall('/api/earnings'),
        ]);
        if (statsRes.status === 'fulfilled' && statsRes.value) {
          const s = statsRes.value;
          setUserStats({ nil_score: s.nil_score ?? 0, subscription_tier: s.subscription_tier, stats: s.stats || {} });
        }
        if (earningsRes.status === 'fulfilled' && earningsRes.value?.success) {
          setUserStats(prev => ({ ...(prev || {}), stats: { ...((prev?.stats) || {}), total_earnings: earningsRes.value.totalEarnings ?? 0 } }));
        }
      } finally {
        window.__nv_preload_shown = true;
        setTimeout(() => setShowInitialLoading(false), 250);
      }
    };
    run();
  }, [user]);

  // ─── Toast auto-dismiss ───────────────────────────────────────────────────

  useEffect(() => { if (success) { const t = setTimeout(() => setSuccess(''), 5000); return () => clearTimeout(t); } }, [success]);
  useEffect(() => { if (error)   { const t = setTimeout(() => setError(''),   5000); return () => clearTimeout(t); } }, [error]);

  // ─── Lazy preload after init ──────────────────────────────────────────────

  useEffect(() => {
    if (!user || !profile) return;
    const t = setTimeout(() => {
      import('./components/pages/PostScheduler');
      setTimeout(() => {
        import('./components/pages/SocialMediaScanner');
        import('./components/pages/GhostAgent');
        import('./components/pages/ProfileSettings');
      }, 1500);
    }, 2000);
    return () => clearTimeout(t);
  }, [user, profile]);

  // ─── Render ───────────────────────────────────────────────────────────────

  const renderContent = () => {
    if (showInitialLoading && !window.__nv_welcome_completed) {
      return <LoadingScreen variant="auth" message="Loading…" isVisible={true} onComplete={() => {}} minDuration={800} />;
    }
    if (showSigningInScreen && (isAuthenticating || authLoading)) {
      return <LoadingScreen variant="auth" message="Signing you in…" isVisible={true} onComplete={() => {}} minDuration={800} />;
    }

    if (!user) {
      if (showAuthScreen) {
        return (
          <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: 'var(--surface-base)' }}>
            <div style={{ flex: 1, paddingBottom: 80 }}>
              <AuthScreen onBack={() => setShowAuthScreen(false)} defaultTab={authDefaultTab} pendingPlan={pendingPlan} />
            </div>
            <Footer sticky />
          </div>
        );
      }
      if (location.pathname === '/') {
        return (
          <LandingPage
            onShowAuth={(tier, tab) => { setPendingPlan(tier || null); setAuthDefaultTab(tab || 'signup'); setShowAuthScreen(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            subscription_tier={null}
          />
        );
      }
      return <Navigate to="/" replace />;
    }

    const commonProps = { user, profile, userStats, setUserStats, setSuccess, setError, setActiveComponent };

    return (
      <div className="app-shell" style={{ display: 'flex', minHeight: '100vh' }}>
        <Sidebar
          activeComponent={activeComponent}
          onNavigate={handleNavigation}
          profile={profile}
          signOut={signOut}
          ghostDraftPending={ghostDraftPending}
          unreadCount={unreadCount}
          onNotifOpen={() => setNotifDrawerOpen(true)}
        />

        <div ref={null} style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }} className="md:ml-[220px]">
          <MobileTopBar
            profile={profile}
            onNavigate={handleNavigation}
            onMenuOpen={() => setMobileNavOpen(true)}
            unreadCount={unreadCount}
            onNotifOpen={() => setNotifDrawerOpen(true)}
          />

          <main className="nv-main-content" style={{ flex: 1 }}>
            <PageTransition pathname={location.pathname}>
              <div style={{ maxWidth: 1152, margin: '0 auto', padding: '0 0 32px' }}>
                <Routes>
                  <Route path="/dashboard"         element={<Dashboard {...commonProps} />} />
                  <Route path="/nil-score"          element={<Lazy><NILScorePage {...commonProps} /></Lazy>} />
                  <Route path="/contract-analyzer"  element={<ContractAnalyzer {...commonProps} />} />
                  <Route path="/earnings"           element={<NILEarningsTracker {...commonProps} />} />
                  <Route path="/post-scheduler"     element={<Lazy><PostScheduler {...commonProps} /></Lazy>} />
                  <Route path="/social-scanner"     element={<Lazy><SocialMediaScanner {...commonProps} /></Lazy>} />
                  <Route path="/ghost-agent"        element={<Lazy><GhostAgent {...commonProps} /></Lazy>} />
                  <Route path="/profile"            element={<Lazy><ProfileSettings {...commonProps} /></Lazy>} />
                  <Route path="/"  element={<Navigate to="/dashboard" replace />} />
                  <Route path="*"  element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </div>
            </PageTransition>
          </main>
        </div>

        <MobileNavDrawer
          isOpen={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
          activeComponent={activeComponent}
          onNavigate={handleNavigation}
          profile={profile}
          signOut={signOut}
          ghostDraftPending={ghostDraftPending}
        />

        <NotificationDrawer
          open={notifDrawerOpen}
          onClose={() => setNotifDrawerOpen(false)}
          notifications={notifications}
          unreadCount={unreadCount}
          onRead={markNotifRead}
          onReadAll={markAllNotifsRead}
          hasMore={notifHasMore}
          onLoadMore={loadMoreNotifs}
        />

        <div className="toast-container">
          <Toast type="success" message={success} />
          <Toast type="error"   message={error}   />
        </div>

        {!tutorialLoading && (
          <Tutorial
            isOpen={showTutorial}
            onClose={() => setShowTutorial(false)}
            onComplete={handleTutorialComplete}
            setActiveComponent={setActiveComponent}
          />
        )}

        <SubscriptionUpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          currentTier={profile?.subscription_tier || 'free'}
        />

        {showIgModal && <InstagramConnectModal onClose={() => setShowIgModal(false)} />}

        <SupportChat />
      </div>
    );
  };

  return (
    <>
      {showWelcomeScreen && (
        <LoadingScreen isVisible={welcomeVisible} onComplete={() => setShowWelcomeScreen(false)} minDuration={3000} />
      )}
      {renderContent()}
    </>
  );
};

// ─── Admin shell ──────────────────────────────────────────────────────────────

const AppAdminContent = () => {
  const { user, profile, apiCall } = useAuth();
  const navigate = useNavigate();
  const [success, setSuccess] = useState('');
  const [error,   setError]   = useState('');

  useEffect(() => { if (success) { const t = setTimeout(() => setSuccess(''), 5000); return () => clearTimeout(t); } }, [success]);
  useEffect(() => { if (error)   { const t = setTimeout(() => setError(''),   5000); return () => clearTimeout(t); } }, [error]);

  if (!user) return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: 'var(--surface-base)' }}>
      <div style={{ flex: 1, paddingBottom: 80 }}><AuthScreen /></div>
      <Footer sticky />
    </div>
  );

  if (!profile) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--surface-base)' }}>
      <div style={{ textAlign: 'center' }}>
        <div className="spinner" style={{ margin: '0 auto 12px' }} />
        <div style={{ color: 'var(--text-secondary)' }}>Loading…</div>
      </div>
    </div>
  );

  if (!profile.is_admin) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem', backgroundColor: 'var(--surface-base)' }}>
      <div style={{ textAlign: 'center' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#fff', marginBottom: 8 }}>Access Denied</h1>
        <p style={{ color: 'var(--text-secondary)', marginBottom: 20 }}>Admin access required</p>
        <button onClick={() => navigate('/dashboard')} className="btn-primary">Return to Dashboard</button>
      </div>
    </div>
  );

  return (
    <React.Suspense fallback={<div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--surface-base)' }}><div className="spinner" /></div>}>
      <AdminPortal user={user} profile={profile} apiCall={apiCall} setSuccess={setSuccess} setError={setError} setActiveComponent={(view) => navigate(`/${view}`)} />
    </React.Suspense>
  );
};

// ─── Root ─────────────────────────────────────────────────────────────────────

const LegalShell = ({ children }) => (
  <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: 'var(--surface-base)' }}>
    {children}
    <Footer />
  </div>
);

const LazyFallback = () => (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--surface-base)' }}>
    <div className="spinner" />
  </div>
);

export default function AppWrapper() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ErrorBoundary>
          <Routes>
            <Route path="/legal/privacy"       element={<React.Suspense fallback={<LazyFallback />}><LegalShell><LegalPrivacy /></LegalShell></React.Suspense>} />
            <Route path="/legal/data-deletion" element={<React.Suspense fallback={<LazyFallback />}><LegalShell><LegalDataDeletion /></LegalShell></React.Suspense>} />
            <Route path="/legal/terms"         element={<React.Suspense fallback={<LazyFallback />}><LegalShell><LegalTerms /></LegalShell></React.Suspense>} />
            <Route path="/legal/patents"       element={<React.Suspense fallback={<LazyFallback />}><LegalShell><LegalPatents /></LegalShell></React.Suspense>} />
            <Route path="/legal/faq"           element={<React.Suspense fallback={<LazyFallback />}><LegalShell><FAQ /></LegalShell></React.Suspense>} />
            <Route path="/admin" element={<AppAdminContent />} />
            <Route path="/*"     element={<AppContent />} />
          </Routes>
        </ErrorBoundary>
      </AuthProvider>
    </BrowserRouter>
  );
}
